import React, { useState, useEffect } from "react";
import {
  TextField,
  FormError,
  Select,
  Loader,
  Button,
  Typography,
} from "components";
import { genderOptions, idOptions } from "utils/constants";
import { useSelector } from "react-redux";
import {
  isSuccessResponse,
  getAgentId,
  notFoundStatus,
  isObjectEmpty,
  getRegionOptions,
} from "utils/helper";
import makeRequest from "services/makeRequest";
import { routes } from "routes/apiRoutes";
import ButtonPair from "./ButtonPair";
import { Formik, Form, ErrorMessage } from "formik";
import { agentGuarantorDetailsValidation } from "utils/FormValidations";

const GuarantorDetails = ({
  handleNext,
  handlePrevious,
  previouslySkipped,
}) => {
  //previouslySkipped is a boolean to cater for having a scenario where a user is
  //having to later provide the guarantor information
  const [loading, setLoading] = useState(false);

  const [existingGuarantorDetails, setExistingGuarantorDetails] = useState({});

  const [stateOptions, setStateOptions] = useState([]);

  const {
    email,
    firstName,
    lastName,
    phoneNumber,
    address,
    region,
    state,
    gender,
    identificationNumber,
    identificationType,
  } = existingGuarantorDetails;

  const {
    user,
    regions: { regions },
  } = useSelector((state) => state);

  //existing agent details would return agentId from token else, we check if there's any in the localstorage
  const agentId = user.user.agent_id || getAgentId();

  const isCreateRequest = () => isObjectEmpty(existingGuarantorDetails);

  const initialValues = {
    email: email || "",
    firstName: firstName || "",
    lastName: lastName || "",
    phoneNumber: phoneNumber || "",
    address: address || "",
    region: region || "",
    state: state || "",
    gender: gender || "",
    identificationType: identificationType || "",
    identificationNumber: identificationNumber || "",
  };

  const getSelectedRegionStates = async (regionName) => {
    try {
      const selectedRegion = regions.find(
        (region) => region.name === regionName
      );
      if (selectedRegion) {
        const { data } = await makeRequest(
          "get",
          routes.fetchRegions(`region=${selectedRegion?.id}&type=1`)
        );
        if (isSuccessResponse(data)) {
          setStateOptions(getRegionOptions(data?.data));
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const getAgentGuarantorDetails = async () => {
      setLoading(true);
      try {
        const { data } = await makeRequest(
          "get",
          routes.guarantorInfo(agentId)
        );

        if (isSuccessResponse(data)) {
          if (data?.data?.length) {
            const agentInfo = {
              ...data?.data[0],
            };

            setExistingGuarantorDetails(agentInfo);

            if (agentInfo.region) {
              getSelectedRegionStates(agentInfo.region);
            }
          }
        }
        setLoading(false);
      } catch (error) {
        if (notFoundStatus(error)) {
          setLoading(false);
        }
      }
    };
    if (!previouslySkipped) getAgentGuarantorDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  useEffect(() => {
    getSelectedRegionStates(existingGuarantorDetails?.region);
    // eslint-disable-next-line
  }, [regions]);

  const onSubmit = async (values) => {
    try {
      let newValues;

      if (!isCreateRequest()) {
        newValues = { ...values };
      } else {
        newValues = {
          details: [values],
          isOnboarded: previouslySkipped ? true : false,
        };
      }

      let method = isCreateRequest() ? "post" : "put";
      let url = isCreateRequest()
        ? routes.guarantorInfo(agentId)
        : routes.updateGuarantorInfo(agentId, existingGuarantorDetails.id);

      const { data } = await makeRequest(method, url, newValues);

      if (isSuccessResponse(data)) {
        handleNext();
      }
    } catch (err) {
      console.log(err);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center onboarding-loader">
        <Loader />
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={() => agentGuarantorDetailsValidation(previouslySkipped)}
      onSubmit={onSubmit}
    >
      {({
        errors,
        touched,
        isSubmitting,
        values,
        setFieldValue,
        handleBlur,
        getFieldProps,
      }) => (
        <Form noValidate autoComplete="off">
          <div className="row">
            <div className="col-md-6">
              <TextField
                name="firstName"
                type="text"
                placeholder="Enter First Name"
                label="Guarantor's First Name"
                {...getFieldProps("firstName")}
                containerClassName={"mt-3"}
                required={false}
                hasError={errors.firstName && touched.firstName}
              />
              <ErrorMessage component={FormError} name="firstName" />
            </div>
            <div className="col-md-6">
              <TextField
                name="lastName"
                type="text"
                placeholder="Enter Last Name"
                label="Guarantor's Last Name"
                {...getFieldProps("lastName")}
                required={false}
                containerClassName={"mt-3"}
                hasError={errors.lastName && touched.lastName}
              />
              <ErrorMessage component={FormError} name="lastName" />
            </div>
            <div className="col-md-6 mt-3">
              <Select
                label="Guarantor's Gender"
                placeholder="Select Gender"
                options={genderOptions}
                name="gender"
                required={false}
                {...getFieldProps("gender")}
                hasError={errors.gender && touched.gender}
              />
              <ErrorMessage component={FormError} name="gender" />
            </div>

            <div className="col-md-6">
              <TextField
                name="email"
                type="text"
                placeholder="Enter email"
                label="Guarantor's Email"
                required={false}
                {...getFieldProps("email")}
                containerClassName={"mt-3"}
                hasError={errors.email && touched.email}
              />
              <ErrorMessage component={FormError} name="email" />
            </div>
            <div className="col-md-6">
              <TextField
                name="phoneNumber"
                type="number"
                maxLength={"11"}
                placeholder="Enter Phone Number"
                label="Guarantor's Phone Number"
                value={values.phoneNumber}
                required={false}
                onChange={(e) => {
                  setFieldValue("phoneNumber", e.target.value); 
                }}
                onBlur={handleBlur}
                // {...getFieldProps("phoneNumber")}
                containerClassName={"mt-3"}
                hasError={errors.phoneNumber && touched.phoneNumber}
              />
              <ErrorMessage component={FormError} name="phoneNumber" />
            </div>

            <div className="col-md-6 mt-3">
              <Select
                label="Guarantor's Region"
                placeholder="Select Region"
                options={getRegionOptions(regions)}
                loading={!regions.length}
                name="region"
                required={false}
                onChange={(e) => {
                  let val = e.target.value;
                  setStateOptions([]);
                  setFieldValue("state", "");
                  setFieldValue("region", val);
                  getSelectedRegionStates(val);
                }}
                value={values.region}
                hasError={errors.region && touched.region}
              />
              <ErrorMessage component={FormError} name="region" />
            </div>
            <div className="col-md-6 mt-3">
              <Select
                label="Guarantor's State"
                placeholder="Select state"
                options={stateOptions}
                disabled={!values.region}
                name="state"
                required={false}
                {...getFieldProps("state")}
                hasError={errors.state && touched.state}
              />
              <ErrorMessage component={FormError} name="state" />
            </div>

            <div className="col-md-6">
              <TextField
                name="address"
                type="text"
                placeholder="Enter Address"
                label="Guarantor's Address"
                {...getFieldProps("address")}
                containerClassName={"mt-3"}
                required={false}
                hasError={errors.address && touched.address}
              />
              <ErrorMessage component={FormError} name="address" />
            </div>
            <div className="col-md-6 mt-3">
              <Select
                label="Identification Type"
                placeholder="Select Identification Type"
                options={idOptions}
                name="identificationType"
                {...getFieldProps("identificationType")}
                required={false}
                hasError={
                  errors.identificationType && touched.identificationType
                }
              />
              <ErrorMessage component={FormError} name="identificationType" />
            </div>

            <div className="col-md-6">
              <TextField
                name="identificationNumber"
                type="text"
                placeholder="Enter Identification Number"
                label="Identification Number"
                {...getFieldProps("identificationNumber")}
                containerClassName={"mt-3"}
                required={false}
                hasError={
                  errors.identificationNumber && touched.identificationNumber
                }
              />
              <ErrorMessage component={FormError} name="identificationNumber" />
            </div>
          </div>
          {previouslySkipped ? (
            <div className="guarantor-submit-cta">
              <Button
                type="submit"
                label="Submit"
                loading={isSubmitting}
                fullWidth
              />
            </div>
          ) : (
            <>
              <ButtonPair
                loading={isSubmitting}
                onPreviousClick={handlePrevious}
              />
              <Typography
                variant="body2"
                state="primary"
                className={"mt-4 text-underline cursor-pointer"}
                onClick={handleNext}
              >
                Click to skip this step and complete later
              </Typography>
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default GuarantorDetails;
